import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Mail, Phone, Youtube, Facebook, Send, CheckCircle } from "lucide-react";
import axios from "axios";

import { API } from "@/config";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [contactInfo, setContactInfo] = useState({ email: "goodsamaritanschoirbwaise@gmail.com", phone: "", location: "Bwaise, Kampala, Uganda" });
  const [socialLinks, setSocialLinks] = useState({ youtube: "https://youtube.com/channel/uc17ljgczqpo4dammdqmvwjw", facebook: "https://www.facebook.com/omusamaliya" });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${API}/settings`);
        if (data.contact_info) setContactInfo(data.contact_info);
        if (data.social_links) setSocialLinks(data.social_links);
      } catch (err) {
        console.error("Failed to load contact settings:", err.message);
      }
    };
    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch on mount; API is a module constant

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-[#1A1A1A] py-20 md:py-28">
        <div className="section-container text-center">
          <p className="text-[#E8922F] text-sm uppercase tracking-widest font-semibold mb-3">Get In Touch</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Contact Us
          </h1>
          <p className="text-gray-400 text-base md:text-lg mt-4 max-w-xl mx-auto">
            We'd love to hear from you. Reach out with questions, prayer requests, or to join our choir.
          </p>
        </div>
      </section>

      <section className="section-padding bg-[#FAFAFA]">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-10">
            <div className="lg:col-span-7">
              <div className="bg-white border border-gray-100 rounded-2xl p-6 md:p-8 shadow-sm">
                {sent ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Message Sent!</h3>
                    <p className="text-[#4A4A4A] text-sm">Thank you for reaching out. We'll get back to you soon.</p>
                    <Button className="btn-secondary mt-6" onClick={() => setSent(false)} data-testid="send-another-btn">Send Another Message</Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Send Us a Message</h3>
                    {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm" data-testid="contact-error">{error}</div>}
                    <div className="space-y-5">
                      <div>
                        <Label htmlFor="name" className="text-sm font-medium text-[#1A1A1A] mb-1.5 block">Your Name</Label>
                        <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="John Doe" className="rounded-xl border-gray-200" data-testid="contact-name-input" />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-sm font-medium text-[#1A1A1A] mb-1.5 block">Email Address</Label>
                        <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="john@example.com" className="rounded-xl border-gray-200" data-testid="contact-email-input" />
                      </div>
                      <div>
                        <Label htmlFor="message" className="text-sm font-medium text-[#1A1A1A] mb-1.5 block">Message</Label>
                        <Textarea id="message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required placeholder="Write your message here..." rows={5} className="rounded-xl border-gray-200" data-testid="contact-message-input" />
                      </div>
                      <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="contact-submit-btn">
                        {loading ? "Sending..." : <><Send size={16} className="mr-2" /> Send Message</>}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="space-y-6">
                <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm" data-testid="contact-info">
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Contact Information</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#FFF3E6] rounded-xl flex items-center justify-center flex-shrink-0"><MapPin size={18} className="text-[#E8922F]" /></div>
                      <div><p className="font-medium text-[#1A1A1A] text-sm">Location</p><p className="text-[#4A4A4A] text-sm">{contactInfo.location}</p></div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#FFF3E6] rounded-xl flex items-center justify-center flex-shrink-0"><Mail size={18} className="text-[#E8922F]" /></div>
                      <div><p className="font-medium text-[#1A1A1A] text-sm">Email</p><p className="text-[#4A4A4A] text-sm">{contactInfo.email}</p></div>
                    </div>
                    {contactInfo.phone && (
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#FFF3E6] rounded-xl flex items-center justify-center flex-shrink-0"><Phone size={18} className="text-[#E8922F]" /></div>
                        <div><p className="font-medium text-[#1A1A1A] text-sm">Phone</p><p className="text-[#4A4A4A] text-sm">{contactInfo.phone}</p></div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm" data-testid="social-links">
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Follow Us</h3>
                  <div className="flex gap-3">
                    <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#FFF3E6] hover:bg-[#E8922F] hover:text-white px-4 py-3 rounded-xl transition-colors text-sm font-medium text-[#1A1A1A]" data-testid="contact-youtube-link">
                      <Youtube size={18} /> YouTube
                    </a>
                    <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#FFF3E6] hover:bg-[#E8922F] hover:text-white px-4 py-3 rounded-xl transition-colors text-sm font-medium text-[#1A1A1A]" data-testid="contact-facebook-link">
                      <Facebook size={18} /> Facebook
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
